import {
  DataResult,
  DataStateChangeEventArgs,
  GridComponent,
  EditSettingsModel,
} from '@syncfusion/ej2-react-grids';
import { useRef, useState, useCallback } from 'react';

import { useNotification } from '@hooks/useNotification';

interface GridService {
  getList: (
    page: number,
    take: number | undefined,
    sortBy: string | undefined,
    sortDirection: string | undefined,
    filters: string
  ) => Promise<any>;
}

interface UseSyncfusionGridOptions {
  service: GridService;
  pageSize?: number;
  queryFilter?: string;
  onError?: (message: string) => void;
  defaultSortBy?: string;
  defaultSortDirection?: string;
}

export const useSyncfusionGrid = ({
  service,
  pageSize = 10,
  queryFilter = '',
  onError,
  defaultSortBy,
  defaultSortDirection,
}: UseSyncfusionGridOptions) => {
  const { showError } = useNotification();

  // Use refs to store latest values without triggering re-renders
  const serviceRef = useRef(service);
  const onErrorRef = useRef(onError);

  // Update refs when props change
  serviceRef.current = service;
  onErrorRef.current = onError;

  // Core grid state
  const [gridData, setGridData] = useState<DataResult>({
    result: [],
    count: 0,
  });
  const [currentPageState, setCurrentPageState] =
    useState<DataStateChangeEventArgs>({
      skip: 0,
      take: pageSize,
    });
  const [gridKey, setGridKey] = useState<number>(0);
  const gridRef = useRef<GridComponent | null>(null);

  // Core data fetching function
  const getData = useCallback(
    async (state: DataStateChangeEventArgs): Promise<DataResult> => {
      const page =
        !state || !state.skip || !state.take ? 1 : state.skip / state.take + 1;

      // Extract sorting parameters from state, fallback to defaults
      let sortBy = defaultSortBy;
      let sortDirection = defaultSortDirection;

      // Handle sorting logic based on state
      if (state.sorted && state.sorted.length > 0) {
        // User clicked to sort
        const firstSort = state.sorted[0];
        sortBy = firstSort.name;
        sortDirection =
          firstSort.direction?.toLowerCase() || defaultSortDirection;
      }
      // For all other cases (no sort, removed sort, initial load), use default sorting

      try {
        const result = await service.getList(
          page,
          state.take,
          sortBy,
          sortDirection,
          queryFilter
        );

        if ('data' in result) {
          return { result: result.data, count: result.total };
        } else {
          const errorMessage = `Failed to fetch data: ${result.errorMessages}`;
          (onError || showError)(errorMessage);
          return { result: [], count: 0 };
        }
      } catch (error) {
        const errorMessage = `Network error: ${error}`;
        (onError || showError)(errorMessage);
        return { result: [], count: 0 };
      }
    },
    [
      service,
      queryFilter,
      onError,
      showError,
      defaultSortBy,
      defaultSortDirection,
    ]
  );

  // Enhanced fetchData with reset capability
  const fetchData = useCallback(
    async (resetToFirstPage = false) => {
      const state = resetToFirstPage
        ? { skip: 0, take: pageSize }
        : currentPageState;
      const data = await getData(state);
      setGridKey((prev) => prev + 1);
      setGridData(data);

      if (resetToFirstPage && gridRef.current) {
        setCurrentPageState({ skip: 0, take: pageSize });
        gridRef.current.goToPage(1);
      }
    },
    [currentPageState, getData, pageSize]
  );

  // Standard grid event handlers
  const renderComplete = useCallback(() => {
    if (
      gridRef.current &&
      gridRef.current.dataSource instanceof Array &&
      !(gridRef.current.dataSource as object[]).length
    ) {
      const state = { skip: 0, take: pageSize };
      // Fetch initial data
      getData(state).then((data) => {
        setGridData(data);
      });
    }
  }, [pageSize, getData]);

  const dataStateChange = useCallback(
    (state: DataStateChangeEventArgs) => {
      setCurrentPageState(state);
      getData(state)
        .then((gridData) => {
          setGridData(gridData);

          // Ensure grid is properly notified that the operation is complete
          // This is crucial for the 3rd click (sort removal) scenario
          if (gridRef.current) {
            // Force grid to recognize the data update
            setTimeout(() => {
              if (gridRef.current && gridRef.current.hideSpinner) {
                gridRef.current.hideSpinner();
              }
            }, 0);
          }
        })
        .catch((error) => {
          console.error('Error in dataStateChange:', error);
          // Set empty data to stop loading spinner
          setGridData({ result: [], count: 0 });
        });
    },
    [getData]
  );

  const execute = useCallback(
    (state: DataStateChangeEventArgs): Promise<DataResult> => {
      setCurrentPageState(state);
      return getData(state);
    },
    [getData]
  );

  // Standard edit options
  const editOptions: EditSettingsModel = {
    allowAdding: true,
    allowDeleting: true,
    allowEditing: true,
    mode: 'Dialog',
  };

  return {
    // State
    gridData,
    gridRef,
    gridKey,

    // Functions
    fetchData,
    renderComplete,
    dataStateChange,
    execute,

    // Grid settings
    editOptions,
    pageSettings: { pageSize, pageSizes: [5, 10, 20, 50, 100] },
  };
};
